import { useCallback, useEffect, useState } from "react";

/**
 * Copy text to the clipboard and track a transient "copied" flag that resets
 * after `resetMs` (default 2000ms). The flag lets a button show a ✓/Copied
 * state without each caller re-implementing the setTimeout dance.
 *
 * @returns `[copied, copy]` — `copied` is the boolean flag, `copy(text)` does
 * the write and starts the reset timer.
 *
 * @example
 * const [copied, copy] = useCopyToClipboard();
 * <button onClick={() => copy(link)}>{copied ? "Copied" : "Copy"}</button>
 */
export function useCopyToClipboard(resetMs = 2000): [boolean, (text: string) => Promise<void>] {
  const [copied, setCopied] = useState(false);
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending reset on unmount so we don't setState after teardown.
  useEffect(() => {
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [timer]);

  const copy = useCallback(
    async (text: string) => {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      const next = setTimeout(() => setCopied(false), resetMs);
      setTimer(next);
    },
    [resetMs],
  );

  return [copied, copy];
}
